import React from 'react';
import {
  Box, Heading, HStack, Icon, Pressable, Text, VStack,
} from '@gluestack-ui/themed';
import { ChevronRight } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { ScheduledChangeSummary } from '@/common/api/models';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import {
  ENGLISH_DATETIME_TEST_ZONE,
  formatEnglishDayMonth,
  formatEnglishTime,
  formatEnglishTimezoneAbbr,
} from '@/common/utils/formatEnglishDateTime';
import { scheduledChangeViews } from './views';
import { ScheduledChangeStateChip } from './components/ScheduledChangeStateChip';
import { splitScheduledChangeList } from './utils/listSections';

interface Props {
  items: ScheduledChangeSummary[];
  onSelect: (changeId: string) => void;
}

/** Matches backend `get_tenant_tz`: Europe/Berlin when locale timezone is missing or UTC. */
export function scheduledChangeDisplayTimeZone(tenantSettings: unknown): string {
  const tz = (tenantSettings as { locale?: { timezone?: string } } | null)?.locale?.timezone;
  if (!tz || tz === 'UTC') {
    return ENGLISH_DATETIME_TEST_ZONE;
  }
  return tz;
}

const IANA_ZONE_ABBR: Record<string, { standard: string; daylight: string }> = {
  'Europe/Berlin': { standard: 'CET', daylight: 'CEST' },
};

function offsetMinutesForZone(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'longOffset',
  }).formatToParts(instant);
  const raw = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const match = raw.match(/GMT(?:(\+|-)(\d{1,2})(?::(\d{2}))?)?/);
  if (!match) return 0;
  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2] ?? 0);
  const mins = Number(match[3] ?? 0);
  return sign * (hours * 60 + mins);
}

function timezoneAbbrForInstant(iso: string, timeZone: string): string {
  const mapping = IANA_ZONE_ABBR[timeZone];
  if (mapping) {
    const instant = new Date(iso);
    const winterInstant = new Date(Date.UTC(instant.getUTCFullYear(), 0, 15, 12, 0, 0));
    const winterOffset = offsetMinutesForZone(winterInstant, timeZone);
    const instantOffset = offsetMinutesForZone(instant, timeZone);
    return instantOffset > winterOffset ? mapping.daylight : mapping.standard;
  }
  return formatEnglishTimezoneAbbr(iso, { timeZone });
}

export function formatScheduledChangeGoLiveClock(
  iso: string,
  timeZone: string,
): { time: string; zone: string } {
  return {
    time: formatEnglishTime(iso, { timeZone }),
    zone: timezoneAbbrForInstant(iso, timeZone),
  };
}

function showsGoLiveClock(state: ScheduledChangeSummary['state']): boolean {
  return state === 'scheduled' || state === 'applying';
}

function isDraftReadyRow(state: ScheduledChangeSummary['state']): boolean {
  return state === 'draft_ready';
}

function listDateLabel(
  item: ScheduledChangeSummary,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (item.state === 'draft_ready') {
    return t('lecturer.scheduledChanges.dateDraftReady');
  }
  const date = item.goLiveAt ? formatEnglishDayMonth(item.goLiveAt) : formatEnglishDayMonth(item.stateChangedAt);
  if (item.state === 'scheduled' || item.state === 'applying') {
    return t('lecturer.scheduledChanges.dateGoesLive', { date });
  }
  if (item.state === 'live') {
    return t('lecturer.scheduledChanges.dateWentLive', { date: formatEnglishDayMonth(item.stateChangedAt) });
  }
  return t('lecturer.scheduledChanges.dateWasDue', { date });
}

function versionLine(item: ScheduledChangeSummary, t: ReturnType<typeof useTranslation>['t']) {
  if (item.state === 'live' && item.appliedVersion && item.appliedVersion !== item.version) {
    return `${t('lecturer.scheduledChanges.version', { version: item.version })} → ${item.appliedVersion}`;
  }
  return t('lecturer.scheduledChanges.version', { version: item.version });
}

export const ScheduledChangesList: React.FC<Props> = ({ items, onSelect }) => {
  const { t } = useTranslation();
  const tenantSettings = useTenantSettingsStore((state) => state.settings);
  const displayTimeZone = scheduledChangeDisplayTimeZone(tenantSettings);
  const { upcoming, recent } = splitScheduledChangeList(items);

  const renderSection = (title: string, hint: string | undefined, rows: ScheduledChangeSummary[]) => {
    if (!rows.length) return null;
    return (
      <VStack space="sm" mb="$4">
        <HStack alignItems="baseline" space="sm">
          <Text size="sm" fontFamily="$heading" color="$textHeading">
            {title}
          </Text>
          {!!hint && (
            <Text size="xs" color="$textHint">
              {hint}
            </Text>
          )}
        </HStack>
        <Box borderWidth={1} borderColor="$borderLight" borderRadius="$md" overflow="hidden">
          {rows.map((item, index) => {
            const view = scheduledChangeViews[item.kind as keyof typeof scheduledChangeViews]
              ?? scheduledChangeViews.oral_exam_question_update;
            return (
              <Pressable
                key={item.id}
                testID={`scheduled-change-row-${item.id}`}
                onPress={() => onSelect(item.id)}
                px="$4"
                py="$3"
                borderTopWidth={index === 0 ? 0 : 1}
                borderTopColor="$borderLight"
                $hover={{ bg: '$bgHover' }}
              >
                <HStack alignItems="center" space="md">
                  <VStack flex={1.2} minWidth={140} space="xs">
                    <Text size="sm" fontFamily="$heading" color="$textHeading" numberOfLines={1}>
                      {item.moduleTitle}
                    </Text>
                    <Text size="xs" color="$textHint">
                      {versionLine(item, t)}
                    </Text>
                  </VStack>
                  <Box flex={1.4} minWidth={160} testID={`scheduled-change-row-${item.id}-summary`}>
                    <Text size="sm" color="$textBody" numberOfLines={2}>
                      {view.summary(item, t)}
                    </Text>
                  </Box>
                  <VStack flexShrink={0} w={128} space="xs">
                    <Text
                      size="sm"
                      color={
                        item.state === 'scheduled' || item.state === 'applying' || isDraftReadyRow(item.state)
                          ? '$textBody'
                          : '$textSubtle'
                      }
                    >
                      {listDateLabel(item, t)}
                    </Text>
                    {showsGoLiveClock(item.state) && !!item.goLiveAt && (
                      <Text
                        size="xs"
                        color="$textSubtle"
                        testID={`scheduled-change-row-${item.id}-clock`}
                      >
                        {t('lecturer.scheduledChanges.goLiveClock', formatScheduledChangeGoLiveClock(item.goLiveAt, displayTimeZone))}
                      </Text>
                    )}
                  </VStack>
                  <ScheduledChangeStateChip state={item.state} kind={item.kind} />
                  <Icon as={ChevronRight} size="sm" color="$textHint" />
                </HStack>
              </Pressable>
            );
          })}
        </Box>
      </VStack>
    );
  };

  return (
    <VStack space="md" px="$6" py="$4">
      <HStack alignItems="baseline" space="sm">
        <Heading size="md">
          {t('lecturer.scheduledChanges.title')}
        </Heading>
        <Text size="sm" color="$textSubtle">
          {t('lecturer.scheduledChanges.count', { count: items.length })}
        </Text>
      </HStack>
      {renderSection(
        t('lecturer.scheduledChanges.upcoming'),
        String(upcoming.length),
        upcoming,
      )}
      {renderSection(
        t('lecturer.scheduledChanges.recent'),
        t('lecturer.scheduledChanges.recentHint'),
        recent,
      )}
    </VStack>
  );
};
